import { useMemo, useCallback, useEffect, useRef, ChangeEvent } from 'react'

import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { MicrosoftVerifiedDomain, UnavailableDomain } from 'types/emailServer'
import {
  toggleMsDomainName,
  setMsDomainName,
  setMsDomainNames,
  getDomainAvailability,
  setShowInlineDeploymentConfirmation,
  setPendingNextStep,
  msVerifyDomain
} from 'redux/features/emailServer/emailServerSlice'
import routesConfig from 'lib/routesConfig'
import { IPIRAppTypes, SetupFlowSteps } from 'components/pages/setupFlow/types/egdWizardTypes'
import { trackEventInAllServices, TRACKING_EVENTS } from 'lib/monitoring/monitoringService'
import { isFailed, isSuccess } from 'redux/toolkit/api'

export interface State {
  msDomainName?: string
  msDomainNames: string[]
  verifiedDomains: MicrosoftVerifiedDomain[]
  availableDomains: string[]
  unavailableDomains: UnavailableDomain[]
  showConfirmDialog: boolean
  isMultiDomainEnabled: boolean
}

export interface EventHandlers {
  onSelectChange: (e: ChangeEvent<HTMLInputElement>) => void
  onToggleDomain: (domain: string) => void
  onSelectAll: () => void
  onDeselectAll: () => void
  onConfirmDeployment: () => void
  onCancelDeployment: () => void
}

export type UseVerifyMsDomainLogic = [State, EventHandlers]

export const useVerifyMsDomainLogic = (): UseVerifyMsDomainLogic => {
  const dispatch = useAppDispatch()

  const {
    msDomainName,
    msDomainNames,
    verifiedDomains,
    availableDomains,
    unavailableDomains,
    getDomainAvailabilitySuccess,
    getDomainAvailabilityFailed,
    showConfirmDialog,
    pendingNextStep,
    saveMsDomainSuccess,
    isMultiDomainEnabled
  } = useAppSelector(_store => ({
    msDomainName: _store.emailServer.msDomainName,
    msDomainNames: _store.emailServer.msDomainNames,
    verifiedDomains: _store.emailServer.emailServerSettings?.microsoftVerifiedDomains,
    availableDomains: _store.emailServer.availableDomains,
    unavailableDomains: _store.emailServer.unavailableDomains,
    getDomainAvailabilitySuccess: isSuccess(_store.emailServer.getDomainAvailabilityApiStatus),
    getDomainAvailabilityFailed: isFailed(_store.emailServer.getDomainAvailabilityApiStatus),
    showConfirmDialog: _store.emailServer.showInlineDeploymentConfirmation,
    pendingNextStep: _store.emailServer.pendingNextStep,
    saveMsDomainSuccess: isSuccess(_store.emailServer.saveMsDomainMethodApiStatus),
    isMultiDomainEnabled: _store.auth.accessTokenObject?.features?.multiDomainSelectionEnabled ?? false
  }))

  // init
  useEffect(
    () => {
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_3_START_MICROSOFT)
      if (!getDomainAvailabilitySuccess && !getDomainAvailabilityFailed) {
        dispatch(getDomainAvailability())
      }
    },
    // eslint-disable-next-line
    []
  )

  // Track if we've done the initial auto-select to prevent re-selecting after user deselects
  const hasInitializedRef = useRef(false)

  // Find the default domain from verifiedDomains
  const defaultDomain = useMemo(() => {
    const defaultVerified = verifiedDomains?.find(d => d.default)
    if (defaultVerified && availableDomains.includes(defaultVerified.name)) {
      return defaultVerified.name
    }
    // Fallback to first available domain if no default is set
    return availableDomains.length > 0 ? availableDomains[0] : null
  }, [verifiedDomains, availableDomains])

  useEffect(() => {
    if (availableDomains.length > 0 && getDomainAvailabilitySuccess && !hasInitializedRef.current) {
      // Only auto-select the default domain on first load, not all domains
      hasInitializedRef.current = true
      if (msDomainNames.length === 0 && defaultDomain) {
        dispatch(setMsDomainNames([defaultDomain]))
        // Also set msDomainName for single-select mode
        dispatch(setMsDomainName(defaultDomain))
      }
    }
  }, [dispatch, getDomainAvailabilitySuccess, availableDomains, msDomainNames, defaultDomain])

  // When multi-domain is disabled, coerce state to single-select mode
  // This also handles the original auto-select behavior: always ensure a domain is selected
  useEffect(() => {
    if (!isMultiDomainEnabled && availableDomains.length > 0) {
      const firstDomain = msDomainNames[0]
      // If no domain selected, auto-select the first available (matches original behavior)
      if (!firstDomain && !msDomainName) {
        const domainToSelect = availableDomains[0]
        dispatch(setMsDomainName(domainToSelect))
        dispatch(setMsDomainNames([domainToSelect]))
      } else if (firstDomain) {
        // Sync msDomainName from msDomainNames[0] if not already set
        if (msDomainName !== firstDomain) {
          dispatch(setMsDomainName(firstDomain))
        }
        // If multiple domains selected, coerce down to just the first one
        if (msDomainNames.length > 1) {
          dispatch(setMsDomainNames([firstDomain]))
        }
      }
      // Clear confirmation dialog state if it was set
      if (showConfirmDialog) {
        dispatch(setShowInlineDeploymentConfirmation(false))
        dispatch(setPendingNextStep(undefined))
      }
    }
  }, [isMultiDomainEnabled, msDomainNames, msDomainName, showConfirmDialog, availableDomains, dispatch])

  // Track if user confirmed deployment (to navigate to IPIR after msVerifyDomain succeeds)
  const confirmedDeploymentRef = useRef(false)

  // After msVerifyDomain succeeds AND user confirmed deployment, navigate to IPIR
  useEffect(() => {
    if (saveMsDomainSuccess && confirmedDeploymentRef.current) {
      confirmedDeploymentRef.current = false
      dispatch(setShowInlineDeploymentConfirmation(false))
      dispatch(setPendingNextStep(undefined))
      routesConfig.SETUP_FLOW_IPIR_AUTHORIZE.goto({}, false, `?appType=${IPIRAppTypes.InlineMx}`)
    }
  }, [saveMsDomainSuccess, dispatch])

  const onSelectChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      dispatch(setMsDomainName(value))
      dispatch(setMsDomainNames([value]))
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_3_SELECT_DOMAIN, { domain: value })
    },
    [dispatch]
  )

  const onToggleDomain = useCallback(
    (domain: string) => {
      dispatch(toggleMsDomainName(domain))
      trackEventInAllServices(TRACKING_EVENTS.SETUP_FLOW.STEP_3_SELECT_DOMAIN, { domain })
    },
    [dispatch]
  )

  const onSelectAll = useCallback(() => {
    dispatch(setMsDomainNames(availableDomains))
  }, [dispatch, availableDomains])

  const onDeselectAll = useCallback(() => {
    dispatch(setMsDomainNames([]))
  }, [dispatch])

  const onConfirmDeployment = useCallback(() => {
    // Multi-domain inline deployment flow:
    // User confirmed, now call msVerifyDomain to create domains and save email_servers
    // After msVerifyDomain succeeds, the useEffect above will navigate to IPIR authorize
    confirmedDeploymentRef.current = true
    dispatch(msVerifyDomain(pendingNextStep as SetupFlowSteps))
  }, [dispatch, pendingNextStep])

  const onCancelDeployment = useCallback(() => {
    dispatch(setShowInlineDeploymentConfirmation(false))
    dispatch(setPendingNextStep(undefined))
  }, [dispatch])

  return useMemo(
    () => [
      {
        msDomainName,
        msDomainNames,
        verifiedDomains: verifiedDomains || [],
        availableDomains,
        unavailableDomains,
        showConfirmDialog,
        isMultiDomainEnabled
      },
      {
        onSelectChange,
        onToggleDomain,
        onSelectAll,
        onDeselectAll,
        onConfirmDeployment,
        onCancelDeployment
      }
    ],
    [
      msDomainName,
      msDomainNames,
      verifiedDomains,
      availableDomains,
      unavailableDomains,
      showConfirmDialog,
      isMultiDomainEnabled,
      onSelectChange,
      onToggleDomain,
      onSelectAll,
      onDeselectAll,
      onConfirmDeployment,
      onCancelDeployment
    ]
  )
}
